import { Vector3 } from "three";

export interface RefAxis {
    origin: Vector3;
    direction: Vector3;
}

// Builds world X/Y/Z reference axes through every point plus one axis along each polygon edge,
// deduplicated by collinearity. Used both while interactively drawing a polygon profile
// (closed=false, since the last->first edge doesn't exist yet) and while dragging a vertex of
// an already-closed profile (closed=true).
export function buildPolygonRefAxes(points: Vector3[], modelRotationOffset: number, closed: boolean): RefAxis[] {
    const vZ = new Vector3(0, 0, 1);
    const vX = new Vector3(1, 0, 0).applyAxisAngle(vZ, modelRotationOffset);
    const vY = vX.clone().applyAxisAngle(vZ, Math.PI / 2);
    const worldAxes = [vX, vY, vZ];

    const axes: RefAxis[] = points.flatMap(origin => worldAxes.map(direction => ({ origin: origin.clone(), direction })));

    const edgeCount = closed ? points.length : points.length - 1;
    for (let i = 0; i < edgeCount; i++) {
        const start = points[i];
        const end = points[(i + 1) % points.length];
        const direction = end.clone().sub(start);
        if (direction.lengthSq() < 1e-10) continue;
        axes.push({ origin: start.clone(), direction: direction.normalize() });
    }

    return dedupeAxes(axes);
}

// Two axes are the same infinite line if their directions are parallel and one's origin lies on
// the other's line — this collapses e.g. a world axis and an edge axis that happen to be
// collinear, or axes shared by coincident points.
function dedupeAxes(axes: RefAxis[]): RefAxis[] {
    const eps = 1e-8;
    const isSameLine = (a: RefAxis, b: RefAxis) => {
        const dirA = a.direction.clone().normalize();
        const dirB = b.direction.clone().normalize();
        if (dirA.clone().cross(dirB).lengthSq() > eps) return false;
        const diff = b.origin.clone().sub(a.origin);
        if (diff.lengthSq() < eps) return true;
        return diff.normalize().cross(dirA).lengthSq() < eps;
    };

    const result: RefAxis[] = [];
    for (const axis of axes) {
        if (!result.some(existing => isSameLine(existing, axis))) {
            result.push(axis);
        }
    }
    return result;
}
